/* eslint-disable no-console */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { RuntimeConfig, initializeRuntimeConfig, setRuntimeConfig } from '@v7/form/src/utils/runtimeConfig';

/**
 * Runtime configuration context
 */
interface RuntimeConfigContextValue {
  config: RuntimeConfig | null;
  isInitialized: boolean;
}

export const RuntimeConfigContext = createContext<RuntimeConfigContextValue>({
  config: null,
  isInitialized: false,
});

/**
 * Hook to access runtime configuration
 * @returns RuntimeConfigContextValue
 */
export const useRuntimeConfig = (): RuntimeConfigContextValue => {
  const context = useContext(RuntimeConfigContext);
  if (!context) {
    throw new Error('useRuntimeConfig must be used within RuntimeConfigProvider');
  }
  return context;
};

interface RuntimeConfigProviderProps {
  children: React.ReactNode;
}

/**
 * Loading Component
 */
function LoadingComponent(): React.ReactElement {
  return (
    <div className="flex h-screen w-screen items-center justify-center" data-testid="runtime-config-loading">
      <FontAwesomeIcon icon={faSpinner as IconProp} spin size="2x" />
    </div>
  );
}

/**
 * Runtime Configuration Provider Component
 *
 * Responsibilities:
 * 1. Fetches runtime configuration from svc_lookup and svc_config endpoints
 *
 * Shows loading screen while initializing
 *
 * Note: Firebase is initialized separately in the auth saga during auth flow
 */
export const RuntimeConfigProvider: React.FC<RuntimeConfigProviderProps> = ({ children }) => {
  const [initState, setInitState] = useState<'initializing' | 'initialized'>('initializing');
  const [config, setConfig] = useState<RuntimeConfig | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async (): Promise<void> => {
      console.log('[RuntimeConfigProvider] Initializing runtime configuration...');
      setInitState('initializing');

      // Step 1: Fetch runtime configuration
      // Note: initializeRuntimeConfig() already handles errors and returns fallback values
      const runtimeConfig = await initializeRuntimeConfig();

      if (!isMounted) return;

      setRuntimeConfig(runtimeConfig);
      setConfig(runtimeConfig);
      setInitState('initialized');
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      config,
      isInitialized: initState === 'initialized',
    }),
    [config, initState],
  );

  // Show loading screen while initializing
  if (initState === 'initializing' || !config) {
    return <LoadingComponent />;
  }

  // Note: We always proceed with config (either fetched or fallback), never block the app
  return <RuntimeConfigContext.Provider value={contextValue}>{children}</RuntimeConfigContext.Provider>;
};

export default RuntimeConfigProvider;
